import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { ButtonsColumn, StyledActionButton } from '@/components/Buttons.tsx'
import { createModal, Modal } from '@/components/Modal'
import {
  ButtonGroup,
  FullScreenContainer,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { BodyM, BodyS, Header1 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { xpertTelNumber } from '@/utils/common.ts'

import { XpertFormStep } from '../../utils/XpertFormSteps.ts'

const Caption = styled(BodyS)`
  margin-top: 16px;
`

const NoReferralModal = ({ onModalClose }: { onModalClose: () => void }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'referralSelection',
  })
  const { trackFormInteraction } = useTrackFormProgress(
    XpertFormStep.ReferralCodeFalse,
    { complete: true },
  )

  const handleModalClose = useCallback(() => {
    onModalClose()
    trackFormInteraction({
      action: 'click',
      formFieldName: 'cancel',
    })
  }, [onModalClose, trackFormInteraction])

  return (
    <Modal title={t('noReferralModal.title')} onClose={handleModalClose}>
      <BodyM>
        {t('noReferralModal.body', {
          phone: xpertTelNumber,
        })}
      </BodyM>
      <ButtonsColumn>
        <StyledActionButton
          variant="Primary"
          href={`tel:${xpertTelNumber}`}
          as="a"
          id="call-xpert"
          onClick={() => {
            trackFormInteraction({
              action: 'click',
              formFieldName: 'call-xpert',
            })
          }}
        >
          {t('noReferralModal.actions.call')}
        </StyledActionButton>
        <StyledActionButton variant="Secondary" onClick={handleModalClose}>
          {t('noReferralModal.actions.cancel')}
        </StyledActionButton>
      </ButtonsColumn>
    </Modal>
  )
}

const ReferralSelection = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'referralSelection',
  })
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [showModal, setShowModal] = useState(false)

  const { trackFormInteraction, triggerManualFormProgress } =
    useTrackFormProgress(XpertFormStep.ReferralCode, { manual: true })

  useEffect(() => {
    if (!showModal) {
      triggerManualFormProgress()
    }
  }, [showModal])

  useEffect(() => {
    const dateOfBirth = params.get('DOB')
    const zdCode = params.get('ZD')
    if (dateOfBirth && zdCode) {
      navigate(`/referral/book/?DOB=${dateOfBirth}&ZD=${zdCode}`)
    }
  }, [params, navigate])

  const handleSelection = useCallback(
    (hasRef: boolean) => {
      if (hasRef) {
        navigate('/referral/book')
      } else {
        setShowModal(true)
      }
    },
    [navigate],
  )

  const handleModalClose = useCallback(() => {
    setShowModal(false)
  }, [])

  return (
    <ScreenWrapper>
      <FullScreenContainer align="center">
        <Header1>{t('header')}</Header1>
        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            id="referral-yes"
            onClick={() => {
              trackFormInteraction({
                action: 'next',
                formFieldName: 'referral-yes',
              })
              handleSelection(true)
            }}
          >
            {t('actions.yes')}
          </StyledActionButton>

          <StyledActionButton
            variant="Secondary"
            id="referral-no"
            onClick={() => {
              trackFormInteraction({
                action: 'click',
                formFieldName: 'referral-no',
              })
              handleSelection(false)
            }}
          >
            {t('actions.no')}
          </StyledActionButton>
        </ButtonGroup>
        <Caption>{t('caption')}</Caption>
        {showModal &&
          createModal(<NoReferralModal onModalClose={handleModalClose} />)}
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { ReferralSelection }
